import React, {useContext, useEffect, useRef, useState} from "react";
import {createQAItem, getQaItem, getQaReplies} from "../qa_forum/qa_api";
import {toast} from "../ui/use-toast";
import {useParams} from "react-router-dom";
import {QAModel, QAReply} from "../qa_forum/QAModel";
import {useAuthInfo} from "@propelauth/react";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {QARespnseCard, ReplyThing} from "../qa_forum/QAThread";
import EditReplyQA from "../qa_forum/EditReplyQA";
import {Button} from "../ui/button";
import {UserContext} from "../../contexts/UserContext";
import {postWithToken} from "../../api/apiBase";

export type BidConversationProps = {
    parent_type: string | null;
    parent_id: string;
}


export function BidConversation({parent_type, parent_id}: BidConversationProps) {
    const {accessToken} = useAuthInfo();
    const {project_id, qa_id} = useParams<{
        project_id: string;
        qa_id: string;
    }>();
    const userContext = useContext(UserContext);
    const limit = 10;
    const [skip, setSkip] = useState(0);
    const [replies, setReplies] = useState<QAReply[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [currentReply, setCurrentReply] = useState<string>("");
    const [editingQA, setEditingQA] = useState<QAModel | QAReply | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadReplies();
    }, [skip]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [replies]);


    const loadReplies = () => {
        if (!project_id || !parent_id) return;

        getQaReplies(project_id, parent_id, accessToken as string, skip, limit)
            .then((newReplies) => {
                setReplies((prevReplies) => {
                    const updatedReplies = [
                        ...prevReplies,
                        ...newReplies.filter((newReply) =>
                            !prevReplies.some((prevReply) => prevReply.id === newReply.id)
                        )
                    ];
                    return updatedReplies;
                })
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: error.message || "Failed to load replies.",
                    variant: "destructive",
                });
            });
    };

    const handleEditQAReply = (qaItem: QAReply) => {
        setEditingQA(qaItem);
    };

    const handleCancelEdit = () => {
        setEditingQA(null);
    };

    const handleSaveEdit = () => {
        setReplies([])
        loadReplies()
        setEditingQA(null)
    };

    const onReply = (text: string) => {
        if (!text.trim()) {
            toast({
                title: "Error",
                description: "Reply cannot be empty.",
                variant: "destructive",
            });
            return;
        }

        if (!parent_id || !project_id || isSubmitting) return;

        const body = {
            parent_id: parent_id,
            parent_type: parent_type,
            is_public: false, //
            body: text,
        };

        setIsSubmitting(true);

        postWithToken(
            `/projects/${project_id}/convo/${parent_type}/create`,
            JSON.stringify(body),
            accessToken as string,
        )
            .then((response) => {
                setReplies((prevReplies) => [...prevReplies, response]);
                setCurrentReply("");
                setIsSubmitting(false);
            })
            .catch((error) => {
                setIsSubmitting(false);
                toast({
                    title: "Error",
                    description: error.message || "Failed to create reply.",
                    variant: "destructive",
                });
            });
    };
    return <div className="flex flex-col h-[calc(100vh-6rem)] sticky top-0 overflow-hidden">
        {/*Area for the replies*/}
        <div className="ml-4 flex-1 min-h-0 flex flex-col">
            <h2 className="text-md font-semibold mb-4">Conversation</h2>
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col pr-3">

                {replies.length > 0 ? (
                    replies.map((qa_reply: QAReply) =>
                        editingQA && editingQA.id === qa_reply.id ? (
                            <EditReplyQA
                                key={qa_reply.id}
                                reply_id={qa_reply.id}
                                onCancel={handleCancelEdit}
                                onSave={handleSaveEdit}
                            />
                        ) : (
                            <QARespnseCard
                                key={qa_reply.id}
                                qa={qa_reply}
                                project_id={project_id as string}
                                size={"text-sm"}
                                controls={{
                                    view: false,
                                    edit: userContext?.email === qa_reply.created_by.email,
                                }}
                                user_company={userContext?.company_id as string}
                                onEdit={handleEditQAReply}
                            />
                        )
                    )
                ) : (
                    <p className="text-gray-500"></p>
                )}
                {replies.length > 10 ?
                    <Button
                        onClick={() => setSkip((prevSkip) => prevSkip + limit)}
                        disabled={replies.length < skip + limit}
                        variant="outline"
                        className="mt-4"
                    >
                        Load More
                    </Button>
                : <></>}
            </div>

        </div>
        <Card className="mt-5">
            <br/>
            <CardContent >
                <ReplyThing
                    saveReply={onReply}
                    value={currentReply}
                    setValue={setCurrentReply}
                />
            </CardContent>
        </Card>
    </div>
}